/**
 * PL AFTER_TAX (PL-PAT) Adjust bridge for BalanceSheet BS-EQ-LFP.
 * Matches ProfitLoss derived AFTER_TAX Adjust# (manual + Auto), computed from PL tree
 * so BS does not depend on ProfitLoss store publish timing.
 */
import {FS_CODE, findNodeByCodes, isCode} from '@/utils/fsCodes';
import {matchesAutoAdjColumn} from '@/utils/fsAdjustmentMultiColumns';
import {resolvePlCotCostOfSalesAdjustmentSum} from '@/utils/plCostOfSalesVisibility';

const PL_COT_EXCLUDED = [
    ...FS_CODE.PL.OPENING_INVENTORIES,
    ...FS_CODE.PL.PURCHASES,
    ...FS_CODE.PL.CLOSING_INVENTORIES,
];

export type PlAfterTaxAdjustBridgeBase = {
    plTree: any[] | null | undefined;
    isAutoAdjEntry: (adj: any) => boolean;
    cotInputMode?: 'COS' | 'PUR';
    showCosRowInAdjustment?: boolean;
};

export type PlAfterTaxAdjustBridgeParams = PlAfterTaxAdjustBridgeBase & {
    refNos: string[];
};

export type PlAfterTaxAutoAdjustItem = {
    /** autoAdjType e.g. TAX | TAX_EXCHANGE; fallback to refNo when missing */
    type: string;
    refNo: string;
};

function sumManualAdjustmentForRefNo(
    node: any,
    refNo: string,
    params: PlAfterTaxAdjustBridgeBase,
): number {
    const kids = Array.isArray(node?.children) ? node.children : [];
    if (kids.length === 0) {
        if (isCode(node, ...FS_CODE.PL.COST_OF_SALES)) {
            return resolvePlCotCostOfSalesAdjustmentSum({
                fsTree: params.plTree || [],
                cotInputMode: params.cotInputMode ?? 'COS',
                showCosRowInAdjustment: params.showCosRowInAdjustment ?? true,
                refNo,
                excludeAuto: true,
                isAutoAdjEntry: params.isAutoAdjEntry,
            });
        }
        if (!Array.isArray(node?.adjustment)) return 0;
        const adj = node.adjustment.find(
            (a: any) => String(a?.refNo) === refNo && !params.isAutoAdjEntry(a),
        );
        return Number(adj?.adjustment ?? 0) || 0;
    }
    const children = isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)
        ? kids.filter((ch: any) => !isCode(ch, ...PL_COT_EXCLUDED))
        : kids;
    let sum = 0;
    for (const child of children) {
        sum += sumManualAdjustmentForRefNo(child, refNo, params);
    }
    return sum;
}

function sumAutoTypeForParent(
    node: any,
    autoType: string,
    autoRefNo: string,
    params: PlAfterTaxAdjustBridgeBase,
): number {
    const kids = Array.isArray(node?.children) ? node.children : [];
    if (kids.length === 0) {
        if (isCode(node, ...FS_CODE.PL.COST_OF_SALES)) {
            return resolvePlCotCostOfSalesAdjustmentSum({
                fsTree: params.plTree || [],
                cotInputMode: params.cotInputMode ?? 'COS',
                showCosRowInAdjustment: params.showCosRowInAdjustment ?? true,
                refNo: autoRefNo,
                autoOnly: true,
                autoAdjType: autoType,
                autoRefNo,
                isAutoAdjEntry: params.isAutoAdjEntry,
            });
        }
        if (!Array.isArray(node?.adjustment)) return 0;
        return node.adjustment
            .filter((a: any) =>
                params.isAutoAdjEntry(a) && matchesAutoAdjColumn(a, autoType, autoRefNo),
            )
            .reduce((s: number, a: any) => s + (Number(a?.adjustment) || 0), 0);
    }
    const children = isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)
        ? kids.filter((ch: any) => !isCode(ch, ...PL_COT_EXCLUDED))
        : kids;
    let sum = 0;
    for (const child of children) {
        sum += sumAutoTypeForParent(child, autoType, autoRefNo, params);
    }
    return sum;
}

function plPatFromGetAdj(getAdj: (codes: string[]) => number): number {
    const gross = getAdj(FS_CODE.PL.TURNOVER) - getAdj(FS_CODE.PL.COST_OF_TURNOVER);
    const grossAndOther = gross + getAdj(FS_CODE.PL.OTHER_INCOME);
    const beforeTax =
        grossAndOther
        - getAdj(FS_CODE.PL.ADMIN_EXPENSES)
        - getAdj(FS_CODE.PL.FINANCE_COSTS);
    return beforeTax - getAdj(FS_CODE.PL.TAXATION);
}

/** Build refNo -> PL-PAT adjustment amount (manual Adjust# only). */
export function computePlAfterTaxAdjustmentsByRefNo(
    params: PlAfterTaxAdjustBridgeParams,
): Record<string, number> {
    const plTree = params.plTree || [];
    if (!plTree.length || !params.refNos.length) return {};

    const map: Record<string, number> = {};
    for (const refNo of params.refNos) {
        const key = String(refNo || '').trim();
        if (!key) continue;
        const getAdj = (codes: string[]) => {
            const node = findNodeByCodes(plTree as any[], codes);
            return node ? sumManualAdjustmentForRefNo(node, key, params) : 0;
        };
        map[key] = plPatFromGetAdj(getAdj);
    }
    return map;
}

/**
 * Build key -> PL-PAT Auto adjustment (Align ProfitLoss computeDerivedAutoAdj for AFTER_TAX).
 * Key = autoAdjType when present, else refNo (same as BalanceSheet auto column identity).
 */
export function computePlAfterTaxAutoAdjustmentsByType(
    params: PlAfterTaxAdjustBridgeBase & {autoItems: PlAfterTaxAutoAdjustItem[]},
): Record<string, number> {
    const plTree = params.plTree || [];
    const items = params.autoItems || [];
    if (!plTree.length || items.length === 0) return {};

    const map: Record<string, number> = {};
    for (const item of items) {
        const autoType = String(item?.type || item?.refNo || '').trim();
        const autoRefNo = String(item?.refNo || '').trim();
        if (!autoType && !autoRefNo) continue;
        const typeKey = autoType || autoRefNo;
        const getAdj = (codes: string[]) => {
            const node = findNodeByCodes(plTree as any[], codes);
            return node ? sumAutoTypeForParent(node, typeKey, autoRefNo || typeKey, params) : 0;
        };
        map[typeKey] = plPatFromGetAdj(getAdj);
    }
    return map;
}
